// src/components/Protect.tsx
"use client";
import React, { useEffect, useState } from "react";
import { authClient } from "@/config/auth";
import { parsePermissionString, PermObject } from "@/utils/auth/permission-utils";

/**
 * Simple in-memory cache to avoid repeated permission calls.
 * Keyed by the original permission string.
 */
const PERM_CACHE = new Map<string, { ts: number; allowed: boolean }>();
const CACHE_TTL = 1000 * 60 * 10; // 10 minutes
const PERM_REQUESTS = new Map<string, Promise<boolean>>();

export type ProtectProps = {
    /**
     * A permission string like "member:invite" or "inbox_read:your"
     * or the structured object format { resource: ["action"] }.
     */
    permission: string | PermObject;

    /**
     * Element to render when permission denied (eg. <Navigate ... />).
     * If not provided, nothing will be rendered.
     */
    fallback?: React.ReactNode;

    /**
     * Optional element to show while permission check is in progress.
     */
    loadingFallback?: React.ReactNode;

    children: React.ReactNode;
};

export function usePermissionString(perm: string | PermObject) {
    const key = typeof perm === "string" ? perm : JSON.stringify(perm);
    const [loading, setLoading] = useState<boolean>(() => {
        const cached = PERM_CACHE.get(key);
        return !cached;
    });
    const [allowed, setAllowed] = useState<boolean>(() => {
        const cached = PERM_CACHE.get(key);
        if (!cached) return false;
        if (Date.now() - cached.ts > CACHE_TTL) {
            PERM_CACHE.delete(key);
            return false;
        }
        return cached.allowed;
    });

    useEffect(() => {
        let mounted = true;
        async function check() {
            setLoading(true);
            try {
                // check cache again inside effect (race safe)
                const cached = PERM_CACHE.get(key);
                if (cached && Date.now() - cached.ts < CACHE_TTL) {
                    if (!mounted) return;
                    setAllowed(cached.allowed);
                    setLoading(false);
                    return;
                }

                // normalize to object
                const permissionObj: PermObject = typeof perm === "string" ? parsePermissionString(perm) : perm;
                const isAllowed = await fetchPermissionStatus(key, permissionObj);

                if (mounted) {
                    setAllowed(isAllowed);
                }
            } catch (err) {
                // treat error as deny
                console.error("permission check error", err);
                if (!mounted) return;
                setAllowed(false);
            } finally {
                if (!mounted) return;
                setLoading(false);
            }
        }

        check();
        return () => {
            mounted = false;
        };
    }, [key, perm]);

    return { loading, allowed };
}

async function fetchPermissionStatus(key: string, permissionObj: PermObject): Promise<boolean> {
    const inFlight = PERM_REQUESTS.get(key);
    if (inFlight) {
        return inFlight;
    }

    const request = authClient.organization
        .hasPermission({ permission: permissionObj })
        .then(({ data, error }) => {
            const allowed = data?.success === true && !error;
            PERM_CACHE.set(key, { ts: Date.now(), allowed });
            PERM_REQUESTS.delete(key);
            return allowed;
        })
        .catch(error => {
            PERM_REQUESTS.delete(key);
            throw error;
        });

    PERM_REQUESTS.set(key, request);
    return request;
}

/**
 * Protect component.
 * Renders children if allowed, otherwise renders fallback.
 */
export default function Protect({ permission, fallback = null, loadingFallback = null, children }: ProtectProps) {
    const permString = typeof permission === "string" ? permission : JSON.stringify(permission);
    const { loading, allowed } = usePermissionString(permission);

    if (loading) {
        return <>{loadingFallback}</>;
    }

    if (!allowed) {
        return <>{fallback}</>;
    }

    return <>{children}</>;
}
